<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import AssignmentPolicyCard from 'dashboard/components-next/AssignmentPolicy/AssignmentPolicyCard/AssignmentPolicyCard.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConfirmDeletePolicyDialog from './components/ConfirmDeletePolicyDialog.vue';
import PolicyPageHeader from './components/PolicyPageHeader.vue';
import PolicyEmptyState from './components/PolicyEmptyState.vue';
import { filterPolicies } from 'dashboard/components-next/AssignmentPolicy/helpers';

const store = useStore();
const { t } = useI18n();
const router = useRouter();

const agentAssignmentsPolicies = useMapGetter(
  'assignmentPolicies/getAssignmentPolicies'
);
const uiFlags = useMapGetter('assignmentPolicies/getUIFlags');
const inboxUiFlags = useMapGetter('assignmentPolicies/getInboxUiFlags');

const confirmDeletePolicyDialogRef = ref(null);

const searchQuery = ref('');

const filteredPolicies = computed(() =>
  filterPolicies(agentAssignmentsPolicies.value, searchQuery.value)
);

const breadcrumbItems = computed(() => [
  {
    label: t('ASSIGNMENT_POLICY.INDEX.HEADER.TITLE'),
    routeName: 'assignment_policy_index',
  },
  { label: t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.HEADER.TITLE') },
]);

const handleBreadcrumbClick = item => {
  router.push({
    name: item.routeName,
  });
};

const onClickCreatePolicy = () => {
  router.push({
    name: 'agent_assignment_policy_create',
  });
};

const onClickEditPolicy = id => {
  router.push({
    name: 'agent_assignment_policy_edit',
    params: {
      id,
    },
  });
};

const handleFetchInboxes = id => {
  if (inboxUiFlags.value.isFetching) return;
  store.dispatch('assignmentPolicies/getInboxes', id);
};

const handleDelete = id => {
  const policy = agentAssignmentsPolicies.value.find(item => item.id === id);
  confirmDeletePolicyDialogRef.value.openDialog(id, policy?.name);
};

const handleDeletePolicy = async policyId => {
  try {
    await store.dispatch('assignmentPolicies/delete', policyId);
    useAlert(
      t(
        'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.DELETE_POLICY.SUCCESS_MESSAGE'
      )
    );
    confirmDeletePolicyDialogRef.value.closeDialog();
  } catch (error) {
    useAlert(
      t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.DELETE_POLICY.ERROR_MESSAGE')
    );
  }
};

onMounted(() => {
  store.dispatch('assignmentPolicies/get');
});
</script>

<template>
  <SettingsLayout :is-loading="uiFlags.isFetching">
    <template #header>
      <PolicyPageHeader
        icon="i-hugeicons-arrow-data-transfer-horizontal"
        :title="
          $t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.HEADER.TITLE')
        "
        :description="$t('ASSIGNMENT_POLICY.REDESIGN.ASSIGNMENT.LIST_INTRO')"
        :breadcrumbs="breadcrumbItems"
        @breadcrumb-click="handleBreadcrumbClick"
      >
        <template #actions>
          <Button
            icon="i-hugeicons-add-01"
            :label="
              $t(
                'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.HEADER.CREATE_POLICY'
              )
            "
            data-test-id="policy-create"
            @click="onClickCreatePolicy"
          />
        </template>
        <Input
          v-if="agentAssignmentsPolicies.length"
          v-model="searchQuery"
          class="w-full sm:max-w-sm"
          :placeholder="
            $t('ASSIGNMENT_POLICY.REDESIGN.COMMON.SEARCH_PLACEHOLDER')
          "
          :aria-label="
            $t('ASSIGNMENT_POLICY.REDESIGN.COMMON.SEARCH_PLACEHOLDER')
          "
        />
        <span
          class="rounded-md bg-n-alpha-2 px-2.5 py-1 text-xs text-n-slate-11 ltr:ml-auto rtl:mr-auto"
        >
          {{
            $t(
              'ASSIGNMENT_POLICY.REDESIGN.COMMON.COUNT',
              { n: agentAssignmentsPolicies.length },
              agentAssignmentsPolicies.length
            )
          }}
        </span>
      </PolicyPageHeader>
    </template>
    <template #body>
      <PolicyEmptyState
        v-if="!agentAssignmentsPolicies.length"
        icon="i-hugeicons-arrow-data-transfer-horizontal"
        :title="$t('ASSIGNMENT_POLICY.REDESIGN.ASSIGNMENT.EMPTY_TITLE')"
        :description="
          $t('ASSIGNMENT_POLICY.REDESIGN.ASSIGNMENT.EMPTY_DESCRIPTION')
        "
        :action-label="
          $t(
            'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.HEADER.CREATE_POLICY'
          )
        "
        @action="onClickCreatePolicy"
      />
      <p
        v-else-if="!filteredPolicies.length"
        class="py-16 text-center text-sm text-n-slate-11"
      >
        {{ $t('ASSIGNMENT_POLICY.REDESIGN.COMMON.NO_RESULTS') }}
      </p>
      <div v-else class="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
        <AssignmentPolicyCard
          v-for="policy in filteredPolicies"
          :key="policy.id"
          v-bind="policy"
          :is-fetching-inboxes="inboxUiFlags.isFetching"
          @fetch-inboxes="handleFetchInboxes"
          @edit="onClickEditPolicy"
          @delete="handleDelete"
        />
      </div>
    </template>
    <ConfirmDeletePolicyDialog
      ref="confirmDeletePolicyDialogRef"
      @delete="handleDeletePolicy"
    />
  </SettingsLayout>
</template>
